import React, { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'
import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  AIDraftStatus,
  EntityTypes,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { AIDraftPromptType } from '@lyrahealth-inc/shared-app-logic/src/models/draft/AIDraft'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { AIStatusBadge } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/aiStatusBadge/AIStatusBadge'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CheckMarkInCircleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckMarkInCircleIcon'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BadgeShape } from '@lyrahealth-inc/ui-core-crossplatform/src/badge/constants'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../../../data/store'
import { useGetFeedbackQuery } from '../../../../clientNotes/data/notesApi'
import PanelFeedbackSection from '../../../../clients/PanelFeedbackSection'
import { InsertMessageOptions } from '../../../BaseChat'
import { useExpireAIDraft } from '../../../data/hooks/useExpireAIDraft'
import { useSelectedClientAIDraftData } from '../../../data/hooks/useSelectedClientAIDraftData'
import { getInsertSmartSuggestionForClientId } from '../../../data/messagesSelectors'
import { setInsertSmartSuggestionForClientId } from '../../../data/messagesSlice'
import { getAiConversationMixpanelProperties } from '../../../data/mixpanelUtils'
import MessengerUtilityItemEmptyState from '../../utilityItem/MessengerUtilityItemEmptyState'

type MessengerSmartSuggestionsSideBarProps = {
  insertMessage: (message: string, opts?: InsertMessageOptions) => void
  onOpenAssignment: (assignmentId: string) => void
  onOpenNotes: (noteId: string) => void
  conversationType?: CONVERSATION_TYPE
}

const Container = styled.View(({ theme }) => ({
  alignItems: 'flex-start',
  padding: `${theme.spacing['16px']} ${theme.spacing['4px']}`,
  gap: theme.spacing['16px'],
}))

const InnerContainer = styled.View(({ theme }) => ({
  alignItems: 'flex-start',
  gap: theme.spacing['24px'],
  paddingLeft: theme.spacing['12px'],
  paddingRight: theme.spacing['12px'],
  width: '100%',
}))

const HeaderSection = styled.View(({ theme }) => ({
  alignItems: 'flex-start',
  gap: theme.spacing['8px'],
}))

const TitleSection = styled.View(({ theme }) => ({
  gap: theme.spacing['4px'],
}))

const DraftContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['16px'],
  border: `1px solid ${theme.colors.borderDefault}`,
  borderRadius: '8px',
  width: '100%',
  gap: theme.spacing['16px'],
}))

const PastedIntoMessage = styled.View(({ theme }) => ({
  flexDirection: 'row',
  backgroundColor: theme.colors.backgroundSuccess,
  borderRadius: '100px',
  alignItems: 'center',
  height: '48px',
  justifyContent: 'center',
  gap: theme.spacing['8px'],
}))

const MessengerSmartSuggestionsSideBar: React.FC<MessengerSmartSuggestionsSideBarProps> = ({
  insertMessage,
  onOpenAssignment,
  onOpenNotes,
  conversationType,
}) => {
  const { colors } = useTheme()
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const { shouldShowPostSessionAIDrafts } = useFlags()
  const { data: aiDraft, isLoading: isLoadingDraft } = useSelectedClientAIDraftData()

  const selectedClientId = useSelector(getSelectedClientId)
  const pasteSmartSuggestionForClientId = useSelector(getInsertSmartSuggestionForClientId)
  useEffect(
    () => () => {
      dispatch(setInsertSmartSuggestionForClientId(null))
    },
    [dispatch],
  )

  const expireAIDraft = useExpireAIDraft()
  useEffect(() => {
    if (pasteSmartSuggestionForClientId === selectedClientId && aiDraft?.summary_text) {
      insertMessage(aiDraft.summary_text, {
        attributesToAttach: {
          gen_ai_submission_tracker_id: aiDraft.id,
        },
        onSend: () => expireAIDraft(aiDraft.id),
      })
    }

    const timeout = setTimeout(() => {
      dispatch(setInsertSmartSuggestionForClientId(null))
    }, 5000)

    return () => clearTimeout(timeout)
  }, [aiDraft, dispatch, expireAIDraft, insertMessage, pasteSmartSuggestionForClientId, selectedClientId])

  const userId = useSelector(getAuthUserId)
  const { data: feedback } = useGetFeedbackQuery(
    aiDraft?.id
      ? {
          providerId: userId,
          entityId: aiDraft.id,
        }
      : skipToken,
  )

  const hasPastedDraft = pasteSmartSuggestionForClientId === selectedClientId
  if (isLoadingDraft) {
    return <LoadingIndicator />
  }

  if (!aiDraft || aiDraft.status !== AIDraftStatus.COMPLETE) {
    return (
      <MessengerUtilityItemEmptyState
        text={
          shouldShowPostSessionAIDrafts ? (
            <FormattedMessage
              defaultMessage='Autogenerated summaries will appear here after each completed intake form and session.'
              description='Empty state for smart suggestions page'
            />
          ) : (
            <FormattedMessage
              defaultMessage='Autogenerated summaries will appear here after each completed intake form.'
              description='Empty state for smart suggestions page'
            />
          )
        }
      />
    )
  }

  return (
    <Container testID={tID('MessengerSmartSuggestionsSideBar-container')}>
      <InnerContainer>
        <HeaderSection>
          <AIStatusBadge
            status={aiDraft.status}
            shape={BadgeShape.RECT}
            customText={intl.formatMessage({
              defaultMessage: 'AI Generated',
              description: 'Badge to let user know that the associated section is generated by AI',
            })}
          />
          <TitleSection>
            <Title
              size={TitleSize.SMALL}
              text={
                aiDraft.prompt_type === AIDraftPromptType.INTAKE ? (
                  <FormattedMessage
                    defaultMessage='Share intake summary with client'
                    description='Header for smart suggestions page for intake'
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage='Share session summary with client'
                    description='Header for smart suggestions page for post-session notes'
                  />
                )
              }
            />
            <Link
              testID={tID('MessengerSmartSuggestionsSideBar-learnMoreLink')}
              text={
                aiDraft.prompt_type === AIDraftPromptType.INTAKE ? (
                  <FormattedMessage defaultMessage='Show full intake' description='Link to view full intake' />
                ) : (
                  <FormattedMessage defaultMessage='View session note' description='Link to view note' />
                )
              }
              underline
              size={BodyTextSize.SMALL}
              onPress={() => {
                if (aiDraft.prompt_type === AIDraftPromptType.INTAKE) {
                  onOpenAssignment(aiDraft.assignment_id)
                } else {
                  onOpenNotes(aiDraft.note_id)
                }
              }}
            />
          </TitleSection>
          <BodyText
            size={BodyTextSize.SMALL}
            text={
              aiDraft.prompt_type === AIDraftPromptType.INTAKE ? (
                <FormattedMessage
                  defaultMessage='This message draft was generated by AI based on the client’s intake form. You are encouraged but not required to use this draft. Please review the message for completeness and accuracy, and make any necessary edits before sending it to your client. It is your responsibility to review and be familiar with the full client intake.'
                  description='Description for smart suggestions page for intake'
                />
              ) : (
                <FormattedMessage
                  defaultMessage='This message draft was generated by AI based on your latest session note. You are encouraged but not required to use this draft. Please review the message for completeness and accuracy, and make any necessary edits before sending it to your client.'
                  description='Description for smart suggestions page for post-session notes'
                />
              )
            }
          />
        </HeaderSection>
        <DraftContainer>
          <BodyText size={BodyTextSize.SMALL} text={aiDraft.summary_text} color={colors.textSecondary} />
          {hasPastedDraft ? (
            <PastedIntoMessage testID={tID('MessengerSmartSuggestionsSideBar-insertedIntoMessage')}>
              <CheckMarkInCircleIcon size={20} fillColor={colors.textSuccess} />
              <BodyText text='Draft inserted' color={colors.textSuccess} />
            </PastedIntoMessage>
          ) : (
            <SecondaryButton
              testID={tID('MessengerSmartSuggestionsSideBar-insertIntoMessageButton')}
              fullWidth
              text={<FormattedMessage defaultMessage='Insert into message' description='Button to insert AI draft' />}
              onPress={() => {
                track({
                  event: mixpanelEvents.INSERT_SMART_SUGGESTION_INTO_MESSAGE,
                  properties: {
                    gen_ai_submission_tracker_id: aiDraft.id,
                    prompt_type: aiDraft.prompt_type,
                    ...getAiConversationMixpanelProperties(conversationType),
                  },
                })
                dispatch(setInsertSmartSuggestionForClientId(selectedClientId))
              }}
            />
          )}
        </DraftContainer>
      </InnerContainer>
      <PanelFeedbackSection
        entityId={aiDraft.id}
        entityType={EntityTypes.AI_DRAFTS}
        promptType={aiDraft.prompt_type}
        removeTopPadding
        isInitialAIDraft={aiDraft.prompt_type === AIDraftPromptType.INTAKE}
        usePerwinkle={false}
        feedback={feedback}
      />
    </Container>
  )
}

export default MessengerSmartSuggestionsSideBar
